import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api, audioUrl, fmtDate, fmtSec, notifyErr } from "../lib/api";
import { CategoryBadge, ReviewBadge, RecStatusBadge, Tone } from "../lib/labels";

const SPK = { agente: "Agente", cliente: "Cliente", canale_sx: "Canale SX", canale_dx: "Canale DX", non_determinato: "Non determinato" };

function Meta({ k, v, testid }) {
  return (
    <div>
      <div className="eyebrow">{k}</div>
      <div className="text-sm mt-0.5 break-all" data-testid={testid}>{v || "—"}</div>
    </div>
  );
}

function ReviewBox({ a, onChanged }) {
  const [notes, setNotes] = useState(a.review_notes || "");
  useEffect(() => setNotes(a.review_notes || ""), [a.id, a.review_notes]);
  const set = async (status) => {
    try {
      await api.put(`/analyses/${a.id}/review`, { status, notes });
      toast.success("Revisione salvata");
      onChanged?.();
    } catch (e) { notifyErr(e); }
  };
  const dnc = a.do_not_contact || a.category === "NON_CONTATTARE";
  return (
    <div className="border rounded-sm p-3 bg-neutral-50" data-testid="review-box">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">Verifica umana</div>
        <ReviewBadge status={a.review_status} testid="review-status-badge" />
      </div>
      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note interne…" className="rounded-sm text-sm bg-white" data-testid="review-notes-input" />
      <div className="flex gap-2 mt-2 flex-wrap">
        <Button size="sm" className="rounded-sm bg-green-600 hover:bg-green-700" disabled={dnc} onClick={() => set("approved")} data-testid="review-approve-btn">Approva</Button>
        <Button size="sm" variant="outline" className="rounded-sm border-red-300 text-red-700 hover:bg-red-50" onClick={() => set("rejected")} data-testid="review-reject-btn">Rifiuta</Button>
        <Button size="sm" variant="outline" className="rounded-sm" onClick={() => set("needs_review")} data-testid="review-needs-btn">Da rivedere</Button>
        <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => set(a.review_status)} data-testid="review-save-notes-btn">Salva note</Button>
      </div>
      {dnc && <p className="text-xs text-red-700 mt-2">Richiesta esplicita di non contatto: non approvabile per liste di recupero.</p>}
    </div>
  );
}

export default function RecordingDrawer({ recordingId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const audio = useRef(null);
  const load = () => api.get(`/recordings/${recordingId}`).then((r) => setD(r.data)).catch(notifyErr);
  useEffect(() => { if (recordingId) { setD(null); load(); } // eslint-disable-next-line
  }, [recordingId]);
  const seek = (t) => { if (audio.current && t != null) { audio.current.currentTime = t; audio.current.play(); } };
  const a = d?.analysis;
  const t = d?.transcription;
  return (
    <Sheet open={!!recordingId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl p-0 overflow-y-auto bg-white" data-testid="recording-drawer">
        <SheetHeader className="px-5 py-4 border-b sticky top-0 bg-white z-10">
          <SheetTitle className="font-head text-lg">Registrazione {d?.phone_number || d?.original_name || ""}</SheetTitle>
          {d && (d.has_audio ? (
            <audio ref={audio} controls preload="metadata" src={audioUrl(d.id)} className="w-full mt-2 h-9" data-testid="audio-player" />
          ) : <p className="text-xs text-neutral-500 mt-2" data-testid="audio-unavailable">Copia audio non disponibile (eliminata o non acquisita).</p>)}
        </SheetHeader>
        {!d ? <div className="p-5 text-sm text-neutral-500">Caricamento…</div> : (
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-3 gap-4">
              <Meta k="Cliente" v={d.customer_name} />
              <Meta k="Telefono" v={d.phone_number} testid="drawer-phone" />
              <Meta k="Lead ID" v={d.lead_id} />
              <Meta k="Campagna" v={d.campaign} />
              <Meta k="Agente" v={d.agent} />
              <Meta k="Esito originale" v={d.disposition} testid="drawer-disposition" />
              <Meta k="Data chiamata" v={fmtDate(d.call_date)} />
              <Meta k="Durata" v={fmtSec(d.duration_sec)} />
              <div><div className="eyebrow">Stato</div><div className="mt-1"><RecStatusBadge status={d.status} /></div></div>
            </div>
            {d.error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 p-2 rounded-sm" data-testid="drawer-error">{d.error}</div>}
            {a && (
              <div className="space-y-3" data-testid="analysis-section">
                <div className="flex items-center gap-2 flex-wrap">
                  <CategoryBadge code={a.category} testid="drawer-category" />
                  <Tone>Affidabilità {Math.round((a.confidence || 0) * 100)}%</Tone>
                  <Tone>Interesse: {a.interest_level}</Tone>
                  {a.callback_requested && <Tone tone="amber">Richiamata: {a.callback_details || "sì"}</Tone>}
                  {a.do_not_contact && <Tone tone="red">NON CONTATTARE</Tone>}
                </div>
                <p className="text-sm leading-relaxed" data-testid="drawer-explanation">{a.explanation}</p>
                {a.agent_issue && <p className="text-sm text-amber-800"><b>Suggerimento di comportamento:</b> {a.agent_issue}</p>}
                {a.suggested_action && (
                  <div className="border border-blue-200 bg-blue-50/50 rounded-sm p-4"
                       data-testid="commercial-recovery-strategy">
                    <div className="font-semibold text-sm text-blue-900 mb-2">
                      Strategia di recupero commerciale
                    </div>
                    <div className="text-sm leading-relaxed text-neutral-800 whitespace-pre-wrap break-words">
                      {a.suggested_action}
                    </div>
                    <div className="text-xs text-neutral-600 mt-3 border-t pt-2">
                      Strategia proposta dall'AI: verificare sempre le informazioni
                      e la legittimità del ricontatto prima di procedere.
                    </div>
                  </div>
                )}
                {a.evidence?.length > 0 && (
                  <div>
                    <div className="eyebrow mb-1.5">Frasi di supporto</div>
                    {a.evidence.map((e, i) => (
                      <button key={i} onClick={() => seek(e.start)} data-testid={`evidence-${i}`}
                        className="block w-full text-left border-l-2 border-[#0047FF] pl-3 py-1 mb-1.5 text-sm hover:bg-blue-50 transition-colors">
                        <span className="font-mono text-xs text-[#0047FF] mr-2">{e.start != null ? fmtSec(e.start) : "--:--"}</span>
                        “{e.text}” {!e.verified && <span className="text-xs text-amber-700">(citazione non verificata)</span>}
                      </button>
                    ))}
                  </div>
                )}
                {a.uncertainty?.length > 0 && (
                  <ul className="text-xs text-neutral-600 list-disc pl-4" data-testid="drawer-uncertainty">
                    {a.uncertainty.map((u, i) => <li key={i}>{u}</li>)}
                  </ul>
                )}
                <div className="text-[11px] text-neutral-500 font-mono">{a.model} · {a.processing_sec}s</div>
                {a.is_opportunity || a.category === "NON_CONTATTARE" ? <ReviewBox a={a} onChanged={() => { load(); onChanged?.(); }} /> : null}
              </div>
            )}
            {t && (
              <div data-testid="transcript-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="eyebrow">Trascrizione</div>
                  <div className="text-[11px] text-neutral-500 font-mono">{t.model} · {t.processing_sec}s · lingua {t.language} · qualità {t.quality}</div>
                </div>
                {t.quality_flags?.length > 0 && <div className="text-xs text-amber-700 mb-2">{t.quality_flags.join(" · ")}</div>}
                <div className="border rounded-sm divide-y max-h-[420px] overflow-y-auto">
                  {(t.segments || []).map((s) => (
                    <div key={s.idx} className="flex gap-3 px-3 py-1.5 text-sm row-hover" data-testid={`segment-${s.idx}`}>
                      <button onClick={() => seek(s.start)} className="font-mono text-xs text-[#0047FF] shrink-0 w-12 text-left hover:underline">{fmtSec(s.start)}</button>
                      <span className="text-[11px] uppercase text-neutral-500 w-20 shrink-0 pt-0.5">{SPK[s.speaker] || s.speaker}</span>
                      <span>{s.text}</span>
                    </div>
                  ))}
                  {!t.segments?.length && <div className="p-3 text-sm text-neutral-500">Nessun parlato rilevato.</div>}
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
